"use client";

import { useEffect } from "react";
import { ExternalLink, LineChart } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { ScrollArea } from "@/shadcn-components/ui/scroll-area";
import { Badge } from "@/shadcn-components/ui/badge";
import { useCustomIndicatorsStore } from "@/lib/custom-indicators/store";
import { useVybeStudioStore } from "@/lib/custom-indicators/studio-store";
import type { CustomIndicatorRecord } from "@/lib/custom-indicators/types";
import CustomIndicatorIcon from "./CustomIndicatorIcon";

interface CustomIndicatorsTabProps {
  isDesktop?: boolean;
  onAddToChart: (record: CustomIndicatorRecord) => void;
}

export default function CustomIndicatorsTab({
  onAddToChart,
}: CustomIndicatorsTabProps) {
  const indicators = useCustomIndicatorsStore((s) => s.indicators);
  const isLoading = useCustomIndicatorsStore((s) => s.isLoading);
  const error = useCustomIndicatorsStore((s) => s.error);
  const reload = useCustomIndicatorsStore((s) => s.reload);
  const requestOpenStudio = useVybeStudioStore((s) => s.requestOpen);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-3 pt-3 pb-2 shrink-0">
        <button
          type="button"
          onClick={requestOpenStudio}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          Create and manage indicators in Vybe Studio
        </button>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="px-3 pb-3 space-y-2">
          {error ? (
            <p className="text-sm text-destructive text-center py-4">{error}</p>
          ) : null}
          {isLoading && indicators.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Loading…
            </p>
          ) : null}
          {!isLoading && indicators.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center rounded-lg border border-dashed border-border">
              No custom indicators yet. Open Vybe Studio to create one.
            </p>
          ) : (
            <ul className="space-y-2">
              {indicators.map((record) => (
                <li
                  key={record.id}
                  className="flex items-center gap-2 rounded-lg border border-border bg-card/40 px-3 py-2"
                >
                  <CustomIndicatorIcon
                    name={record.name}
                    iconDataUrl={record.iconDataUrl}
                    size={36}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {record.name}
                      </span>
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 shrink-0"
                      >
                        v{record.version}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {record.overlay ? "Overlay" : "Pane"} · Updated{" "}
                      {new Date(record.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    title="Add to chart"
                    onClick={() => onAddToChart(record)}
                  >
                    <LineChart className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
